import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import {
  ConfigService,
  EntityService,
  TokenService
} from 'viggo-sistemas-core-web';
import { IPedidoParte, PedidoParteEvento } from '../model/pedidos-parte.model';

@Injectable()
export class PedidoParteService extends EntityService<IPedidoParte> {
  constructor(
    protected configService: ConfigService,
    protected tokenService: TokenService
  ) {
    super({
      baseEntityEndPoint: 'pedido_parte',
      urlBackend: configService.backendURL
    });
  }

  /**
   * Gera um objeto evento que será inserido dentro do array de eventos
   * @returns objeto evento
   */
  initializeDataEvento(type: string) {
    return {
      status: type,
      status_dh: new Date(),
      status_por: this.tokenService.getUser().id
    } as PedidoParteEvento;
  }

  /**
   * Gera um objeto evento que será inserido dentro do array de eventos
   * @returns objeto evento
   */
  initializeDataEtapaMultiplas(
    etapa_id: string,
    partes: { id: string; quantidade: number }[]
  ) {
    return {
      etapa_id: etapa_id,
      etapa_dh: new Date(),
      partes: partes
    };
  }

  gerarEvento(id: string, type: string): Observable<any> {
    const url = `${this.baseURL}/${id}/reg_evento`;
    return this.http.put(url, this.initializeDataEvento(type), {
      headers: this.getHeader()
    });
  }

  /**
   * Envia as partes do pedido para a prox etapa
   */
  enviarMultiplosParaEtapa(body: any): Observable<any> {
    const url = `${this.baseURL}/reg_etapa_multiplas`;
    return this.http.put(
      url,
      { ...body, etapa_dh: new Date() },
      {
        headers: this.getHeader()
      }
    );
  }

  /**
   * Finaliza a etapa para aquela parte do pedido
   */
  finalizaEtapa(body: any): Observable<any> {
    const url = `${this.baseURL}/finaliza_etapas`;
    return this.http.put(url, body, {
      headers: this.getHeader()
    });
  }

  /**
   * Finaliza a etapa para aquela parte do pedido
   */
  confirmarConclusao(body: any): Observable<any> {
    const url = `${this.baseURL}/concluir_etapas`;
    return this.http.put(url, body, {
      headers: this.getHeader()
    });
  }

  /**
   * Inicia a produção na etapa para aquela parte do pedido
   */
  iniciarProducaoNaEtapa(body: any): Observable<any> {
    const url = `${this.baseURL}/iniciar_etapas`;
    return this.http.put(url, body, {
      headers: this.getHeader()
    });
  }

  /**
   * Função responsável por listar as etapas disponiveis por parte
   * @param parte_pedido_id
   * @param pagination
   * @param include
   * @param domain_id
   * @param where
   * @returns
   */
  etapasDisponiveis(
    parte_pedido_id: string,
    pagination: {
      require_pagination?: boolean;
      page_size?: number;
      page?: number;
    },
    include: string[],
    domain_id: string,
    linha_producao_id: string,
    where?: string | { field: string; value: string },
    order_by?: string
  ) {
    var url = `${this.baseURL}/${parte_pedido_id}/etapas_abertas`;
    var params = new HttpParams();

    if (pagination) {
      params = params
        .append('page', pagination.page)
        .append('page_size', pagination.page_size)
        .append('require_pagination', pagination.require_pagination);
    }

    if (include) params = params.append('include', include.join(','));

    if (domain_id) params = params.append('domain_id', domain_id);

    if (linha_producao_id)
      params = params.append('linha_producao_id', linha_producao_id);

    if (order_by) params = params.append('order_by', order_by);

    if (where) {
      if (typeof where === 'object' && Object.keys(where).length !== 0)
        params = params.append(`${where.field}`, where.value);
      else if (typeof where === 'string')
        params = params.append('etapa_id', where);
    }

    return this.http.get<any>(url, {
      params: params,
      headers: this.getHeader()
    });
  }

  /**
   * Função responsável por listar as etapas disponiveis por parte
   * @param parte_pedido_id
   * @param pagination
   * @param include
   * @param domain_id
   * @param where
   * @returns
   */
  etapasDisponiveisMultiplas(
    pedido_parte_ids: string[],
    pagination: {
      require_pagination?: boolean;
      page_size?: number;
      page?: number;
    },
    include: string[],
    domain_id: string,
    linha_producao_id: string,
    where?: string | { field: string; value: string },
    order_by?: string,
    filters?: any
  ) {
    var url = `${this.baseURL}/etapas_abertas_multiplas`;
    var params = new HttpParams();

    // if (pedido_parte_ids)
    //   params = params.append('pedido_parte_ids', pedido_parte_ids.join(','));

    if (pagination) {
      params = params
        .append('page', pagination.page)
        .append('page_size', pagination.page_size)
        .append('require_pagination', pagination.require_pagination);
    }

    if (include) params = params.append('include', include.join(','));

    if (domain_id) params = params.append('domain_id', domain_id);

    if (linha_producao_id)
      params = params.append('modelo.linha_producao_id', linha_producao_id);

    if (order_by) params = params.append('order_by', order_by);

    if (where) {
      if (typeof where === 'object' && Object.keys(where).length !== 0)
        params = params.append(`${where.field}`, where.value);
      else if (typeof where === 'string')
        params = params.append('etapa_id', where);
    }

    const body: any = { ...filters };

    params.keys().forEach(key => {
      const values = params.getAll(key);
      body[key] = values.length > 1 ? values : values[0];
    });

    return this.http.post<any>(
      url,
      { ...body, ...filters, require_pagination: true, pedido_parte_ids },
      {
        headers: this.getHeader()
      }
    );
  }

  /**
   * Função responsável por listar as etapas disponiveis por parte
   * @param url_material
   * @param modelo_id
   * @param pagination
   * @param include
   * @param domain_id
   * @param where
   * @returns
   */
  materiaisDisponiveis(
    url_material: string,
    modelo_id: string,
    pagination: {
      require_pagination?: boolean;
      page_size?: number;
      page?: number;
    },
    include: string[],
    domain_id: string,
    categoria?: string,
    where?: string | { field: string; value: string }
  ) {
    var url = `${this.baseURL}/${url_material}`;
    var params = new HttpParams();

    if (pagination) {
      params = params
        .append('page', pagination.page)
        .append('page_size', pagination.page_size)
        .append('require_pagination', pagination.require_pagination);
    }

    if (include) params = params.append('include', include.join(','));

    if (modelo_id) params = params.append('modelo_id', modelo_id);

    if (domain_id) params = params.append('domain_id', domain_id);

    if (categoria)
      params = params.append(
        'categoria_materia_prima.classificacao',
        categoria
      );

    if (where) {
      if (typeof where === 'object' && Object.keys(where).length !== 0)
        params = params.append(`${where.field}`, where.value);
      else if (typeof where === 'string')
        params = params.append('pedido_parte_id', where);
    }

    return this.http.get<any>(url, {
      params: params,
      headers: this.getHeader()
    });
  }

  /**
   * Voltar a etapa para aquela parte do pedido
   */
  voltarEtapa(id: string): Observable<any> {
    const url = `${this.baseURL}/${id}/voltar_etapa`;
    return this.http.put(url, null, {
      headers: this.getHeader()
    });
  }
}
